import React from 'react';
import { Link } from 'react-router-dom';
import { RotateCcw, ArrowLeft, Mail, Calendar, Clock, CheckCircle, XCircle, AlertTriangle, CreditCard } from 'lucide-react';

const Refund = () => {
  return (
    <div className="min-h-screen bg-[#0a0f1e]">
      {/* Header */}
      <header className="border-b border-[#1e3a5f30] bg-[#0a0f1e]/90 backdrop-blur-xl sticky top-0 z-50">
        <div className="container mx-auto px-6 py-4">
          <div className="flex items-center justify-between">
            <Link to="/" className="flex items-center gap-3 text-white hover:text-[#00d4aa] transition-colors">
              <ArrowLeft className="w-5 h-5" />
              <span className="font-semibold">Back to Home</span>
            </Link>
            <div className="flex items-center gap-2">
              <RotateCcw className="w-5 h-5 text-[#00d4aa]" />
              <span className="text-white font-medium">Legal</span>
            </div>
          </div>
        </div>
      </header>

      {/* Content */}
      <main className="container mx-auto px-6 py-12 max-w-4xl">
        <div className="bg-[#111827] border border-[#1e3a5f30] rounded-2xl p-8 md:p-12">
          {/* Title */}
          <div className="text-center mb-12">
            <div className="inline-flex items-center justify-center w-16 h-16 rounded-full bg-[#00d4aa]/10 mb-6">
              <RotateCcw className="w-8 h-8 text-[#00d4aa]" />
            </div>
            <h1 className="text-3xl md:text-4xl font-bold text-white mb-4">Refund Policy</h1>
            <p className="text-[#94a3b8]">Our commitment to your satisfaction</p>
          </div>

          {/* Last Updated */}
          <div className="flex items-center gap-2 text-[#94a3b8] text-sm mb-8 justify-center">
            <Calendar className="w-4 h-4" />
            <span>Last Updated: March 2026</span>
          </div>

          {/* 7-Day Guarantee Banner */}
          <div className="bg-gradient-to-r from-[#00d4aa]/20 to-[#00d4aa]/10 border border-[#00d4aa]/30 rounded-2xl p-6 mb-8">
            <div className="flex flex-col md:flex-row items-center gap-4 text-center md:text-left">
              <div className="w-16 h-16 rounded-full bg-[#00d4aa] flex items-center justify-center flex-shrink-0">
                <span className="text-2xl font-bold text-[#0a0f1e]">7</span>
              </div>
              <div>
                <h2 className="text-xl font-bold text-white mb-1">7-Day Money Back Guarantee</h2>
                <p className="text-[#94a3b8]">
                  For <strong className="text-[#00d4aa]">first-time subscribers only</strong>. Try AILeadMantra risk-free!
                </p>
              </div>
            </div>
          </div>

          {/* Sections */}
          <div className="space-y-8">
            {/* Eligibility */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">1</span>
                Refund Eligibility
              </h2>
              <ul className="text-[#94a3b8] space-y-4">
                <li className="flex items-start gap-3">
                  <CheckCircle className="w-5 h-5 text-[#00d4aa] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">First-time subscribers</strong> can request a full refund within <strong className="text-[#00d4aa]">7 days</strong> of their initial purchase.</span>
                </li>
                <li className="flex items-start gap-3">
                  <XCircle className="w-5 h-5 text-[#ef4444] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">No refunds after 7 days</strong> - Refund requests made after the 7-day period will not be honored.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
                  <span><strong className="text-white">Renewals are non-refundable</strong> - This policy applies only to first-time purchases, not subscription renewals.</span>
                </li>
              </ul>
            </section>

            {/* Cancellation */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">2</span>
                Cancellation Policy
              </h2>
              <div className="bg-[#0a0f1e] rounded-lg p-4 border border-[#1e3a5f]">
                <p className="text-[#94a3b8] leading-relaxed">
                  When you cancel your subscription, your account will remain <strong className="text-white">active until the end of your current billing period</strong>. You will continue to have full access to all features until that date.
                </p>
              </div>
              <ul className="text-[#94a3b8] mt-4 space-y-2">
                <li className="flex items-center gap-2">
                  <Clock className="w-4 h-4 text-[#00d4aa]" />
                  No immediate loss of access upon cancellation
                </li>
                <li className="flex items-center gap-2">
                  <CreditCard className="w-4 h-4 text-[#00d4aa]" />
                  No further charges after cancellation
                </li>
                <li className="flex items-center gap-2">
                  <RotateCcw className="w-4 h-4 text-[#00d4aa]" />
                  You can reactivate anytime
                </li>
              </ul>
            </section>

            {/* Refund Process */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#00d4aa]/20 flex items-center justify-center text-[#00d4aa] text-sm font-bold">3</span>
                Refund Process
              </h2>
              <div className="space-y-4">
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#3b82f6] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">1</div>
                  <div>
                    <p className="text-white font-medium">Submit Request</p>
                    <p className="text-[#94a3b8] text-sm">Email us at hello@aileadmantra.com with your account details and reason for refund.</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#3b82f6] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">2</div>
                  <div>
                    <p className="text-white font-medium">Verification</p>
                    <p className="text-[#94a3b8] text-sm">Our team will verify your eligibility (first-time subscriber, within 7 days).</p>
                  </div>
                </div>
                <div className="flex items-start gap-4">
                  <div className="w-8 h-8 rounded-full bg-[#00d4aa] flex items-center justify-center text-white font-bold text-sm flex-shrink-0">3</div>
                  <div>
                    <p className="text-white font-medium">Processing Time</p>
                    <p className="text-[#94a3b8] text-sm">Approved refunds will be processed within <strong className="text-[#00d4aa]">5-7 business days</strong>.</p>
                  </div>
                </div>
              </div>
            </section>

            {/* Important Notes */}
            <section className="bg-[#1a2235] rounded-xl p-6 border border-[#1e3a5f30]">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <span className="w-8 h-8 rounded-lg bg-[#f59e0b]/20 flex items-center justify-center text-[#f59e0b] text-sm font-bold">!</span>
                Important Notes
              </h2>
              <ul className="text-[#94a3b8] space-y-3">
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
                  <span>Refunds are credited to the original payment method used during purchase.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
                  <span>Processing time may vary depending on your bank or payment provider.</span>
                </li>
                <li className="flex items-start gap-3">
                  <AlertTriangle className="w-5 h-5 text-[#f59e0b] mt-0.5 flex-shrink-0" />
                  <span>Abuse of the refund policy may result in account restrictions.</span>
                </li>
              </ul>
            </section>

            {/* Contact */}
            <section className="bg-gradient-to-r from-[#00d4aa]/10 to-[#00d4aa]/5 rounded-xl p-6 border border-[#00d4aa]/20">
              <h2 className="text-xl font-semibold text-white mb-4 flex items-center gap-2">
                <Mail className="w-5 h-5 text-[#00d4aa]" />
                Request a Refund
              </h2>
              <p className="text-[#94a3b8] leading-relaxed">
                To request a refund or if you have any questions about this policy, please contact us at:
              </p>
              <a 
                href="mailto:hello@aileadmantra.com" 
                className="inline-flex items-center gap-2 mt-3 text-[#00d4aa] hover:text-[#00b894] font-medium transition-colors"
              >
                <Mail className="w-4 h-4" />
                hello@aileadmantra.com
              </a>
            </section>
          </div>
        </div>
      </main>

      {/* Footer */}
      <footer className="border-t border-[#1e3a5f30] bg-[#0a0f1e] py-8 mt-12">
        <div className="container mx-auto px-6">
          <div className="flex flex-col md:flex-row justify-between items-center gap-6">
            <div className="text-center md:text-left">
              <p className="text-white font-semibold">© 2026 AILeadMantra</p>
              <p className="text-[#94a3b8] text-sm">B2B SaaS Platform</p>
            </div>
            <div className="flex flex-wrap justify-center gap-4 text-sm">
              <Link to="/" className="text-[#94a3b8] hover:text-white transition-colors">Home</Link>
              <span className="text-[#1e3a5f]">|</span>
              <a href="/#robots" className="text-[#94a3b8] hover:text-white transition-colors">Features</a>
              <span className="text-[#1e3a5f]">|</span>
              <a href="/#pricing" className="text-[#94a3b8] hover:text-white transition-colors">Pricing</a>
              <span className="text-[#1e3a5f]">|</span>
              <a href="mailto:hello@aileadmantra.com" className="text-[#94a3b8] hover:text-white transition-colors">Contact</a>
            </div>
            <div className="flex gap-4 text-sm">
              <Link to="/terms" className="text-[#94a3b8] hover:text-white transition-colors">Terms</Link>
              <span className="text-[#1e3a5f]">|</span>
              <Link to="/privacy" className="text-[#94a3b8] hover:text-white transition-colors">Privacy</Link>
              <span className="text-[#1e3a5f]">|</span>
              <Link to="/refund" className="text-[#00d4aa] font-medium">Refund Policy</Link>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
};

export default Refund;
